import { Injectable, Injector } from '@angular/core';
import { MarkerConfigShared } from '../../models/interface/markerConfig.interface';
import { MarkerType } from '../../models/interface/markerType.enum';
import { MuLayoutPageAndMapService } from '../../sharedProtos/layout/mu-layout-page-and-map.service';
import * as mapStyle from '../../../../../assets/mapStyle/mapStyle.json';
import { MapOptionsShared } from '../../models/interface/mapOptions.interface';
import { sharedMapConstants } from '../../sharedConstants/map.contants';
import { MarkerTypeName } from '../../models/interface/markerTypeName.enum';
import { MarkerTypeColor } from '../../models/interface/markerTypeColor.enum';
import { MarkerTypeClassName } from '../../models/interface/markerTypeClassName.enum';
import { Actions } from '../../types/actions.type';
import { MuMapActions } from '../../sharedModules/designSystem/mu-map/mu-map.actions';
import { MuGeologicService } from '../../sharedProtos/geologic/mu-geologic.service';
import { AddressGeocodeShared } from '../../models/interface/addressGeocode.interface';
import { MuException } from '../../types/exception.type';
import { environment } from 'src/environments/environment';
import { MarkerTypeTooltip } from '../../models/interface/markerTypeToolltip.enum';
import { MarkerTypeTooltipIcon } from '../../models/interface/markerTypeToolltipIcon.enum';
import { TranslateService } from '@ngx-translate/core';
import { MarkerConfig } from 'src/app/components/favorite-address/models/interface/markerConfig.interface';
import { MuMapComponent } from '../../sharedModules/designSystem/mu-map/mu-map.component';
import { CoordinateShared } from '../../models/interface/coordinate.interface';
import { MuMapAtlasService } from '../../sharedModules/designSystem/mu-atlas/services/mu-map-atlas.service';
import {
  AtlasIcon,
  AtlasLabel,
  AtlasPoint,
  AtlasPopup,
  AtlasSize,
} from '../../sharedModules/designSystem/mu-atlas/interfaces/mu-atlas-types.interface';
import { AtlasMarker } from '../../sharedModules/designSystem/mu-atlas/interfaces/mu-atlas-marker.interface';

@Injectable({
  providedIn: 'root',
})
export class MuMapMarkerService {
  private muLayoutPageAndMapService: MuLayoutPageAndMapService;
  private mapOptions: MapOptionsShared;
  private muGeologicService: MuGeologicService;
  private translateService: TranslateService;
  private muMapAtlasService: MuMapAtlasService;
  private actions: Actions<MuMapActions, any> = new Actions<MuMapActions, any>();

  constructor(private injector: Injector) {
    this.muLayoutPageAndMapService =
      this.injector.get<MuLayoutPageAndMapService>(MuLayoutPageAndMapService);
    this.muGeologicService = this.injector.get<MuGeologicService>(MuGeologicService);
    this.translateService = this.injector.get<TranslateService>(TranslateService);
    this.muMapAtlasService = this.injector.get<MuMapAtlasService>(MuMapAtlasService);
    this.mapOptions = sharedMapConstants.mapOptions;
    // eslint-disable-next-line @typescript-eslint/dot-notation
    this.mapOptions.styles = mapStyle['default'];
  }

  get actionList(): Actions<MuMapActions, any> {
    return this.actions;
  }

  buildMarkerListeners(marker: any): void {
    let markerConfig = this.getMarkerConfig(marker);
    if (markerConfig && markerConfig?.type !== MarkerType.DASHBOARD) {
      if (marker.removeAllListeners) {
        marker.removeAllListeners();
      }
    }
    marker.addListener('dragstart', () => {
      this.muLayoutPageAndMapService.setIsDrag(true);
      markerConfig = { ...markerConfig, name: null, type: MarkerType.DRAG_START };
      marker = this.setMarkerConfig(marker, markerConfig);
      this.buildMarker(marker);
    });
    marker.addListener('dragend', async (event: any) => {
      this.muLayoutPageAndMapService.setIsDrag(false);
      markerConfig = {
        ...markerConfig,
        labelOrigin: null,
        mapAddress: null,
        type: MarkerType.LOADING,
      };
      marker = this.setMarkerConfig(marker, markerConfig);
      this.buildMarker(marker);
      const position = this.muMapAtlasService
        .createBounds(event.latLng ?? event.target.getLatLng())
        .getCenter();
      await this.getAddressByCoordinates(marker, position.lat, position.lng);
    });
    marker.addListener('click', (event: any) => {
      this.actionList.emit(MuMapActions.CLICK_MARKER, { event, marker });
    });
  }

  buildMarker(marker: any, markerList?: any[]): void {
    let markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
    this.setAndGetIcon(marker);
    this.setAndGetLabel(marker);
    this.setPosition(marker);
    this.setTooltip(marker);

    if (markerConfig?.popup) {
      this.setPopup(marker);
    }
    this.setZoomChangeListener(marker);
    if (markerList) {
      const markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
      markerConfig?.mapComponent.updateMarkers(markerList);
      markerConfig?.mapComponent.cleanFitBounds(markerConfig.zoomPadding);
    }
  }

  setPosition(marker: any): void {
    const markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
    const coordinates = markerConfig?.mapAddress?.coordinates;
    if (coordinates?.latitude && coordinates?.longitude) {
      marker.setPosition({ lat: coordinates.latitude, lng: coordinates.longitude });
    }
    if (markerConfig?.zoom && markerConfig.mapComponent) {
      markerConfig.mapComponent?.fitBounds(false, 100, markerConfig.zoom);
    }
  }

  setAndGetIcon(marker: any): any {
    let markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
    let icon: AtlasIcon;

    switch (markerConfig?.type?.toString()) {
      case MarkerType.STORE.toString():
        markerConfig = { ...markerConfig, name: MarkerTypeName.STORE, type: MarkerType.STORE };
        marker = this.setMarkerConfig(marker, markerConfig);
        icon = this.getIconCommon(marker);

        break;
      case MarkerType.DRAG_START.toString():
        icon = this.getIconDragStart(marker);

        break;
      case MarkerType.COVERAGE.toString():
        markerConfig = { ...markerConfig, color: MarkerTypeColor.GRAY };
        marker = this.setMarkerConfig(marker, markerConfig);
        icon = this.getIconBlank(marker);

        break;
      case MarkerType.DASHBOARD.toString():
        marker = this.setMarkerConfig(marker, markerConfig);
        icon = this.getIconDashboard(marker);

        break;
      default:
        icon = this.getIconBlank(marker);

        break;
    }
    marker.setIcon(icon);
    return icon;
  }

  setAndGetLabel(marker: any): any {
    const markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
    let label: AtlasLabel;

    switch (markerConfig?.type?.toString()) {
      case MarkerType.CURRENT.toString():
        label = this.getLabelCurrent();

        break;
      case MarkerType.DRAG_START.toString():
        label = this.getLabelShadow();

        break;
      case MarkerType.LOADING.toString():
        label = this.getLabelLoading();

        break;
      case MarkerType.NUMBER_LABEL.toString():
        label = this.getNumberLabel(marker);
        label = this.setAndGetTooltipCommonClass(marker, label);

        break;
      case MarkerType.DASHBOARD.toString():
        label = this.getNumberLabel(marker);
        label = this.setAndGetTooltipCommonClass(marker, label);

        break;
      default:
        label = this.getLabelCommon();
        label = this.setAndGetTooltipCommonClass(marker, label);

        break;
    }

    marker.setLabel(label);
    return label;
  }

  setPopup(marker: any) {
    const markerConfig: MarkerConfigShared = this.getMarkerConfig(marker);
    let popup: AtlasPopup;
    popup = markerConfig.popup;
    marker = marker.setPopup(popup);
    return popup;
  }

  private getIconBlank(marker: AtlasMarker): AtlasIcon {
    const markerConfig = this.getMarkerConfig(marker);
    const iconName =
      (markerConfig?.name || MarkerTypeName.BLANK) +
      '-' +
      (markerConfig?.color || MarkerTypeColor.BLUE);
    const url = this.getUrlPath(iconName);
    const size: AtlasSize = !markerConfig?.scaledSize
      ? { width: this.mapOptions.commonMarkerWidth, height: this.mapOptions.commonMarkerHeight }
      : { width: this.mapOptions.customMarkerWidth, height: this.mapOptions.customMarkerHeight };
    const labelOrigin: AtlasPoint = !markerConfig?.labelOrigin ? { x: 1, y: 1 } : { x: 18, y: 17 };

    return this.muMapAtlasService.createIcon({ url, size, labelOrigin, className: '' });
  }

  private getIconDashboard(marker: AtlasMarker): AtlasIcon {
    const markerConfig = this.getMarkerConfig(marker);
    const iconName =
      (markerConfig?.name || MarkerTypeName.BLANK) +
      '-' +
      (markerConfig?.color || MarkerTypeColor.BLUE);
    const url = this.getUrlPath(iconName);
    const size: AtlasSize = {
      width: markerConfig.width,
      height: markerConfig.heigth,
    };
    const labelOrigin: AtlasPoint = !markerConfig?.labelOrigin ? { x: 1, y: 1 } : { x: 18, y: 17 };

    return this.muMapAtlasService.createIcon({
      url,
      size,
      labelOrigin,
      className: markerConfig.className,
    });
  }

  private getIconCommon(marker: AtlasMarker): AtlasIcon {
    const markerConfig = this.getMarkerConfig(marker);
    const iconName =
      (markerConfig?.name || MarkerTypeName.FAVORITE) +
      '-' +
      (markerConfig?.color || MarkerTypeColor.BLUE);
    const url = this.getUrlPath(iconName);
    const size: AtlasSize = !markerConfig?.scaledSize
      ? { width: this.mapOptions.commonMarkerWidth, height: this.mapOptions.commonMarkerHeight }
      : { width: this.mapOptions.customMarkerWidth, height: this.mapOptions.customMarkerHeight };
    const labelOrigin: AtlasPoint = !markerConfig?.labelOrigin ? { x: 1, y: 1 } : { x: 18, y: 17 };

    return this.muMapAtlasService.createIcon({ url, size, labelOrigin, className: '' });
  }

  private getIconDragStart(marker: AtlasMarker): AtlasIcon {
    const markerConfig = this.getMarkerConfig(marker);
    const iconName =
      (markerConfig?.name || MarkerTypeName.DRAG_START.toString()) +
      '-' +
      (markerConfig?.color || MarkerTypeColor.BLUE);
    const url = this.getUrlPath(iconName);
    const size: AtlasSize = {
      width: this.mapOptions.commonMarkerWidth,
      height: this.mapOptions.commonMarkerHeight,
    };
    const labelOrigin: AtlasPoint = { x: 1, y: 5 };
    return this.muMapAtlasService.createIcon({ url, size, labelOrigin, className: '' });
  }

  private getLabelCurrent(): AtlasLabel {
    const text = ' ';
    const className = MarkerTypeClassName.MARKER + ' ' + MarkerTypeClassName.DOT;

    return this.muMapAtlasService.createLabel({ text, className });
  }

  private getLabelCommon(): AtlasLabel {
    const color = '';
    const text = ' ';
    const fontFamily = 'Arial';
    const className = '';

    return this.muMapAtlasService.createLabel({ text, color, fontFamily, className });
  }

  private getLabelShadow(): AtlasLabel {
    const color = '';
    const text = ' ';
    const fontFamily = 'Arial';
    const className = MarkerTypeClassName.BASE + MarkerTypeClassName.SHADOW;

    return this.muMapAtlasService.createLabel({ text, color, fontFamily, className });
  }

  private getLabelLoading(): AtlasLabel {
    const color = '';
    const text = '';
    const fontFamily = 'Arial';
    const className = MarkerTypeClassName.BASE + MarkerTypeClassName.LOADING;

    return this.muMapAtlasService.createLabel({ text, color, fontFamily, className });
  }

  private getNumberLabel(marker: AtlasMarker): AtlasLabel {
    const markerConfig = this.getMarkerConfig(marker);
    const color = markerConfig.colorLabel ? markerConfig.colorLabel : '#FFFFFF';
    const text = markerConfig?.label;
    const fontFamily = 'Arial';
    const className = '';

    return this.muMapAtlasService.createLabel({ text, color, fontFamily, className });
  }

  private setAndGetTooltipCommonClass(marker: AtlasMarker, label: AtlasLabel): AtlasLabel {
    const markerConfig = this.getMarkerConfig(marker);
    let className = markerConfig?.tooltip?.text
      ? MarkerTypeClassName.BASE + markerConfig?.tooltip?.type + ' '
      : ' ';
    className += markerConfig?.tooltip?.icon ? markerConfig?.tooltip?.icon + ' ' : ' ';
    return this.muMapAtlasService.createLabel({ ...label, className });
  }

  setTooltip(marker: any): void {
    const markerConfig = this.getMarkerConfig(marker);
    if (!markerConfig?.tooltip) {
      return environment.guardClause;
    }
    setTimeout(() => {
      if (!markerConfig.multiTooltip) {
        const element = document.getElementsByClassName(
          markerConfig?.tooltip?.type
        )[0] as HTMLElement;
        if (element) {
          element.setAttribute('data-tooltipText', markerConfig?.tooltip.text);
          element.classList.add(MarkerTypeTooltip.TEXT_ICON + '-show');
        }
      } else {
        const element = document.getElementById(
          `leaflet-tooltip-${{ ...marker }.marker._tooltip._leaflet_id}`
        );
        if (element) {
          const item = element.querySelector(`.${markerConfig?.tooltip?.type}`);
          item.setAttribute('data-tooltipText', markerConfig?.tooltip.text);
          item.classList.add(MarkerTypeTooltip.TEXT_ICON + '-show');
        }
      }
    }, 900);
  }

  getUrlPath(iconName: string, color?: MarkerTypeColor): string {
    return (
      this.mapOptions.markerUrl +
      iconName +
      (color ? '-' + color : '') +
      this.mapOptions.markerExtension
    );
  }

  getMarkerConfig(marker: any): MarkerConfigShared {
    return marker.get('markerConfig') as MarkerConfig;
  }

  setMarkerConfig(marker: any, markerConfig: MarkerConfigShared): any {
    marker.setValues({ markerConfig });
    return marker;
  }

  setZoomChangeListener(marker: any): void {
    const markerConfig = this.getMarkerConfig(marker);
    const self = this;
    if (!markerConfig?.mapComponent?.map?.addListener) {
      return;
    }
    markerConfig?.mapComponent?.map.addListener('zoom_changed', () => {
      setTimeout(() => {
        self.setTooltip(marker);
      }, 200);
    });
  }

  async getAddressByCoordinates(marker: any, latitude: number, longitude: number): Promise<void> {
    let markerConfig = this.getMarkerConfig(marker);
    markerConfig?.addressControl?.setValue('');
    this.muLayoutPageAndMapService.setIsDrag(false);
    try {
      let response: AddressGeocodeShared;
      if (markerConfig.validateCity) {
        response = await this.muGeologicService.getAddressByCoordinatesWithCity(
          markerConfig?.countryGeoName,
          latitude,
          longitude,
          markerConfig?.cityControl.value
        );
      }

      if (!markerConfig?.validateCity) {
        response = await this.muGeologicService.getAddressByCoordinates(
          markerConfig?.countryGeoName,
          latitude,
          longitude
        );

        if (response.cityGeoName && !markerConfig?.notSetCity) {
          const cityData = markerConfig?.cityList?.find(
            city =>
              city.name?.trim()?.toLowerCase() === response.cityGeoName.trim()?.toLowerCase() ||
              city.geoName?.trim()?.toLowerCase() === response.cityGeoName.trim().toLowerCase()
          );
          markerConfig?.cityControl?.setValue(cityData?.id || markerConfig?.cityControl?.value, {
            emitEvent: false,
          });
        }
      }

      if (!response.address || response.address?.length < 1) {
        throw new MuException({});
      }

      if (!markerConfig?.notSetAddress) {
        markerConfig?.addressControl?.setValue(response.address);
      }
      response = { ...response, marker };
      this.actions.emit(MuMapActions.SEND_ADDRESS, response);
    } catch (e) {
      markerConfig = {
        ...markerConfig,
        type: MarkerType.COVERAGE,
        mapAddress: { coordinates: { latitude, longitude } },
      };

      marker = this.setMarkerConfig(marker, markerConfig);
      this.drawPolygon(marker);
      this.actions.emit(MuMapActions.FAIL_GET_ADDRESS, {
        message: 'addressCoverage',
        key: 'addressCoverage',
        position: markerConfig?.markerId,
      });
    }
  }

  async drawPolygon(marker: any, updateCoordinates?: boolean): Promise<void> {
    let markerConfig = this.getMarkerConfig(marker);
    const cityId = markerConfig?.cityControl?.value;
    if (!cityId || !markerConfig?.mapComponent) {
      return environment.guardClause;
    }
    if (markerConfig.type === MarkerType.COVERAGE) {
      const text = this.translateService.instant('tooltip.mapMaker.coverage');
      markerConfig = {
        ...markerConfig,
        tooltip: {
          text,
          type: MarkerTypeTooltip.TEXT_ICON,
          icon: MarkerTypeTooltipIcon.WARN,
        },
      };
    }
    marker = this.setMarkerConfig(marker, markerConfig);
    const polygon = await this.muGeologicService.getPolygon(cityId, environment.production ? 0 : 1);
    markerConfig?.mapComponent.createPolygon({ polygon, marker });
    if (updateCoordinates) {
      markerConfig?.mapComponent.focusOn(polygon, 11);
      const coordinates = this.getCenterCoordinates(polygon);
      marker.setPosition({ lat: coordinates.latitude, lng: coordinates.longitude });
    }
    this.setTooltip(marker);
  }

  async centerByPolygon(cityId: number, mapComponent: MuMapComponent<any>): Promise<void> {
    const polygon = await this.muGeologicService.getPolygon(cityId, environment.production ? 0 : 1);
    const coordinates = this.getCenterCoordinates(polygon);
    mapComponent.map.setCenter({ lat: coordinates.latitude, lng: coordinates.longitude });
  }

  getCenterCoordinates(polygon: any): CoordinateShared {
    const bounds = this.muMapAtlasService.createBounds();
    for (const d of polygon.getPath()) {
      bounds.extend(d);
    }
    return {
      latitude: bounds?.getCenter()?.lat || 0,
      longitude: bounds?.getCenter()?.lng || 0,
    };
  }

  setMapOptions(mapOptions: MapOptionsShared): void {
    this.mapOptions = mapOptions;
  }
}
